import { z } from 'zod';
import { IncidentSeverity } from './incidents.js';
import { Address } from './participants.js';
export const RecallStatus = z.enum(['open', 'closed']);
/** Each affected unit is tracked through its own resolution trail. */
export const AffectedUnitStatus = z.enum(['notified', 'acknowledged', 'remediated', 'verified', 'closed']);
/**
 * A recall is raised by CER against an approved model, optionally narrowed to
 * production batches. Units in scope are resolved and snapshotted when it is
 * raised; the model may be withdrawn in the same action.
 */
export const RecallInput = z
    .object({
    modelId: z.string(),
    batches: z.array(z.string().min(1).max(100)).min(1).max(50).optional(),
    incidentId: z.string().optional(),
    severity: IncidentSeverity,
    title: z.string().min(3).max(200),
    description: z.string().min(10).max(5000),
    remedy: z.string().min(10).max(5000),
    withdrawModel: z.boolean().default(false),
})
    .strict();
export const RecallEvent = z.object({
    /** Surrogate id of the event. @metadata technical @source incidents.recall_events.id @transform bigint to number */
    id: z.number().int(),
    /** Kind of event: raised, note, status, units_added or model_withdrawn. @metadata business @source incidents.recall_events.kind */
    kind: z.enum(['raised', 'note', 'status', 'units_added', 'model_withdrawn']),
    /** Status before a status change. @metadata business @source incidents.recall_events.from_status */
    fromStatus: RecallStatus.nullable(),
    /** Status after a status change. @metadata business @source incidents.recall_events.to_status */
    toStatus: RecallStatus.nullable(),
    /** Note recorded with the event. @metadata business @source incidents.recall_events.note */
    note: z.string().nullable(),
    /** User who recorded the event. @metadata technical @source incidents.recall_events.actor_id */
    actorId: z.string().nullable(),
    /** Display name of that user. @metadata technical @source iam.users.display_name via incidents.recall_events.actor_id @transform left join */
    actorName: z.string().nullable(),
    /** When the event was recorded. @metadata technical @source incidents.recall_events.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
});
export const AffectedUnitEvent = z.object({
    /** Surrogate id of the event. @metadata technical @source incidents.recall_unit_events.id @transform bigint to number */
    id: z.number().int(),
    /** Kind of event: notified, note or status. @metadata business @source incidents.recall_unit_events.kind */
    kind: z.enum(['notified', 'note', 'status']),
    /** Resolution status before a status change. @metadata business @source incidents.recall_unit_events.from_status */
    fromStatus: AffectedUnitStatus.nullable(),
    /** Resolution status after a status change. @metadata business @source incidents.recall_unit_events.to_status */
    toStatus: AffectedUnitStatus.nullable(),
    /** Note recorded with the event. @metadata business @source incidents.recall_unit_events.note */
    note: z.string().nullable(),
    /** Reference to supporting evidence, e.g. of remediation. @metadata business @source incidents.recall_unit_events.evidence_ref */
    evidenceRef: z.string().nullable(),
    /** User who recorded the event. @metadata technical @source incidents.recall_unit_events.actor_id */
    actorId: z.string().nullable(),
    /** Display name of that user. @metadata technical @source iam.users.display_name via incidents.recall_unit_events.actor_id @transform left join */
    actorName: z.string().nullable(),
    /** Participant the user acted for. @metadata technical @source incidents.recall_unit_events.actor_participant_id */
    actorParticipantId: z.string().nullable(),
    /** Legal name of that participant. @metadata business @source register.participants.legal_name via incidents.recall_unit_events.actor_participant_id @transform left join */
    actorParticipantName: z.string().nullable(),
    /** When the event was recorded. @metadata technical @source incidents.recall_unit_events.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
});
/** Snapshot of a unit as it stood when the recall resolved it, plus its live resolution status. */
export const AffectedUnit = z.object({
    /** Surrogate id of the affected-unit record. @metadata technical @source incidents.recall_affected_units.id @transform bigint to number */
    id: z.number().int(),
    /** Id of the recall. @metadata technical @source incidents.recall_affected_units.recall_id */
    recallId: z.string(),
    /** Id of the affected unit. @metadata technical @source incidents.recall_affected_units.unit_id */
    unitId: z.string(),
    /** Serial number of the unit when the recall resolved it. @metadata business @source incidents.recall_affected_units.serial @transform snapshot taken when the unit was added to the recall */
    serial: z.string(),
    /** Batch of the unit when the recall resolved it. @metadata business @source incidents.recall_affected_units.batch @transform snapshot taken when the unit was added to the recall */
    batch: z.string().nullable(),
    /** Lifecycle status of the unit when the recall resolved it. @metadata business @source incidents.recall_affected_units.unit_status_at_recall @transform snapshot taken when the unit was added to the recall */
    unitStatusAtRecall: z.string(),
    /** Id of the unit's active installation at that time. @metadata technical @source incidents.recall_affected_units.installation_id @transform snapshot */
    installationId: z.string().nullable(),
    /** NMI of that installation. @metadata business @source incidents.recall_affected_units.nmi @transform snapshot */
    nmi: z.string().nullable(),
    /** Site address of that installation. @metadata business @source incidents.recall_affected_units.address @transform snapshot; parsed through Address */
    address: Address.nullable(),
    /** Participant id of the unit's owner at that time. @metadata technical @source incidents.recall_affected_units.owner_participant_id @transform snapshot */
    ownerParticipantId: z.string(),
    /** Legal name of the owner. @metadata business @source register.participants.legal_name via incidents.recall_affected_units.owner_participant_id @transform joined; current name, not a snapshot */
    ownerParticipantName: z.string(),
    /** Participant id of the installer at that time. @metadata technical @source incidents.recall_affected_units.installer_participant_id @transform snapshot */
    installerParticipantId: z.string().nullable(),
    /** Legal name of the installer. @metadata business @source register.participants.legal_name via incidents.recall_affected_units.installer_participant_id @transform left join; current name, not a snapshot */
    installerParticipantName: z.string().nullable(),
    /** Participant responsible for resolving the unit. @metadata technical @source incidents.recall_affected_units.responsible_participant_id */
    responsibleParticipantId: z.string(),
    /** Legal name of the responsible participant. @metadata business @source register.participants.legal_name via incidents.recall_affected_units.responsible_participant_id @transform joined */
    responsibleParticipantName: z.string(),
    /** Resolution status: notified, acknowledged, remediated, verified or closed. @metadata business @source incidents.recall_affected_units.status */
    status: AffectedUnitStatus,
    /** When the unit was added to the recall. @metadata technical @source incidents.recall_affected_units.added_at @transform timestamptz as an ISO 8601 UTC string */
    addedAt: z.string(),
    /** When the record was last changed. @metadata technical @source incidents.recall_affected_units.updated_at @transform timestamptz as an ISO 8601 UTC string */
    updatedAt: z.string(),
    /** The unit's resolution trail. @metadata business @source derived: incidents.recall_unit_events rows for the affected unit */
    events: z.array(AffectedUnitEvent),
});
export const AffectedUnitCounts = z.object({
    /** Units in the recall. @metadata business */
    total: z.number().int(),
    /** Units notified but not yet acknowledged. @metadata business */
    notified: z.number().int(),
    /** Units acknowledged. @metadata business */
    acknowledged: z.number().int(),
    /** Units remediated. @metadata business */
    remediated: z.number().int(),
    /** Units verified by CER. @metadata business */
    verified: z.number().int(),
    /** Units closed. @metadata business */
    closed: z.number().int(),
});
export const RecallSummary = z.object({
    /** Surrogate id of the recall. @metadata technical @source incidents.recalls.id */
    id: z.string(),
    /** Recall status: open or closed. @metadata business @source incidents.recalls.status */
    status: RecallStatus,
    /** Severity: low, medium, high or critical. @metadata business @source incidents.recalls.severity */
    severity: IncidentSeverity,
    /** Short title of the recall. @metadata business @source incidents.recalls.title */
    title: z.string(),
    /** Id of the recalled model. @metadata technical @source incidents.recalls.model_id */
    modelId: z.string(),
    /** Model number of the recalled model. @metadata business @source register.device_models.model_number via incidents.recalls.model_id @transform joined from the model */
    modelNumber: z.string(),
    /** Current approval status of the recalled model. @metadata business @source register.device_models.status via incidents.recalls.model_id @transform joined from the model */
    modelStatus: z.string(),
    /** Participant id of the model's manufacturer. @metadata technical @source register.device_models.manufacturer_id via incidents.recalls.model_id @transform joined from the model */
    manufacturerId: z.string(),
    /** Legal name of the manufacturer. @metadata business @source register.participants.legal_name via register.device_models.manufacturer_id @transform joined from the manufacturer's participant record */
    manufacturerName: z.string(),
    /** Production batches the recall is narrowed to; null for every batch. @metadata business @source incidents.recalls.batches */
    batches: z.array(z.string()).nullable(),
    /** Id of the incident the recall was raised from, if any. @metadata technical @source incidents.recalls.incident_id */
    incidentId: z.string().nullable(),
    /** Number of affected units in each resolution status. @metadata business @source derived: count(incidents.recall_affected_units) by status @transform grouped count, as at the event */
    counts: AffectedUnitCounts,
    /** When the recall was closed. @metadata business @source incidents.recalls.closed_at @transform timestamptz as an ISO 8601 UTC string */
    closedAt: z.string().nullable(),
    /** When the recall was raised. @metadata technical @source incidents.recalls.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
    /** When the recall was last changed. @metadata technical @source incidents.recalls.updated_at @transform timestamptz as an ISO 8601 UTC string */
    updatedAt: z.string(),
});
export const Recall = RecallSummary.extend({
    /** Full description of the defect. @metadata business @source incidents.recalls.description */
    description: z.string(),
    /** What responsible participants must do. @metadata business @source incidents.recalls.remedy */
    remedy: z.string(),
    /** CER user who raised the recall. @metadata technical @source incidents.recalls.raised_by_user_id */
    raisedByUserId: z.string().nullable(),
    /** Display name of that user. @metadata technical @source iam.users.display_name via incidents.recalls.raised_by_user_id @transform left join */
    raisedByUserName: z.string().nullable(),
    /** The recall's trail of events. @metadata business @source derived: incidents.recall_events rows for the recall */
    events: z.array(RecallEvent),
    /** Every unit in scope of the recall. @metadata business @source derived: incidents.recall_affected_units rows for the recall */
    affectedUnits: z.array(AffectedUnit),
});
export const RecallList = z.object({ items: z.array(RecallSummary) });
export const RecallQuery = z.object({
    status: RecallStatus.optional(),
    modelId: z.string().optional(),
    unitId: z.string().optional(),
    q: z.string().max(200).optional(),
});
export const RecallNote = z.object({ note: z.string().min(1).max(5000) }).strict();
export const RecallClose = z.object({ note: z.string().min(3).max(5000) }).strict();
/**
 * Progress one affected unit. The responsible participant acknowledges and
 * remediates; CER verifies and closes (and may close a unit outright, e.g.
 * one found to be out of scope, with the reason recorded).
 */
export const AffectedUnitStatusChange = z
    .object({
    status: z.enum(['acknowledged', 'remediated', 'verified', 'closed']),
    note: z.string().min(3).max(5000),
    evidenceRef: z.string().max(500).optional(),
})
    .strict();
export const AffectedUnitNote = z.object({ note: z.string().min(1).max(5000), evidenceRef: z.string().max(500).optional() }).strict();
//# sourceMappingURL=recalls.js.map